/**
 * InsightsExportService
 *
 * Exports selected actors with their JTBD subcollections as JSON
 * for offline analysis via the insights-analyzer skill.
 *
 * This service is the single source of truth for building the actor payload.
 * The same payload structure will be reused by the backend agent endpoint.
 *
 * @module insights-analyzer/services
 */

import { ActorService } from '@modules/firebase-core/services/ActorService';
import { AudienceService } from '@modules/firebase-core/services/AudienceService';
import type { InsightItem } from '@segmently/schema-models/entities';
import type {
  InsightsExportEnvelope,
  InsightsAnalysisPayload,
  ExportedActor,
  ExportedInsightItem,
  JtbdCategory,
} from '../types';

// Re-export types for consumer convenience
export type { InsightsExportEnvelope, InsightsAnalysisPayload, ExportedActor };

// ── Helpers ──────────────────────────────────────────────────────────

const CATEGORIES: JtbdCategory[] = [
  'coreJobs', 'contexts', 'fears', 'bigJobs', 'subtasks', 'competitors', 'habits',
];

function stripInsightItem(item: InsightItem): ExportedInsightItem {
  return {
    id: item.id ?? '',
    description: item.description ?? '',
    weight: item.weight ?? 1,
  };
}

function countInsightItems(actor: ExportedActor): number {
  return CATEGORIES.reduce((sum, cat) => sum + (actor[cat]?.length ?? 0), 0);
}

// ── Service ──────────────────────────────────────────────────────────

export class InsightsExportService {
  /**
   * Build the core payload for a set of selected actors.
   * This method is reusable — called by both export (file download) and
   * future backend agent requests.
   *
   * @param projectId - Project ID
   * @param projectName - Human-readable project name
   * @param actorSelections - Array of { actorId, audienceId } pairs
   */
  async buildPayload(
    projectId: string,
    projectName: string,
    actorSelections: Array<{ actorId: string; audienceId: string }>,
  ): Promise<InsightsAnalysisPayload> {
    // Group selections by audience for efficient loading
    const byAudience = new Map<string, string[]>();
    for (const sel of actorSelections) {
      if (!byAudience.has(sel.audienceId)) byAudience.set(sel.audienceId, []);
      byAudience.get(sel.audienceId)!.push(sel.actorId);
    }

    // Load audience names + actors with subcollections in parallel
    const audienceService = new AudienceService(projectId);
    const exportedActors: ExportedActor[] = [];

    for (const [audienceId, actorIds] of byAudience.entries()) {
      const actorService = new ActorService(projectId, audienceId);

      // Load audience name
      const audience = await audienceService.get(audienceId);
      const audienceName = audience?.name ?? audienceId;

      // Load all actors with subcollections in parallel
      const actorsWithSubs = await Promise.all(
        actorIds.map(id => actorService.getWithSubcollections(id)),
      );

      for (const actor of actorsWithSubs) {
        if (!actor) continue;

        exportedActors.push({
          id: actor.id ?? '',
          name: actor.name ?? '',
          description: actor.description ?? '',
          audienceId,
          audienceName,
          weight: actor.weight ?? 1,
          coreJobs: (actor.coreJobs ?? []).map(stripInsightItem),
          contexts: (actor.contexts ?? []).map(stripInsightItem),
          fears: (actor.fears ?? []).map(stripInsightItem),
          bigJobs: (actor.bigJobs ?? []).map(stripInsightItem),
          subtasks: (actor.subtasks ?? []).map(stripInsightItem),
          competitors: (actor.competitors ?? []).map(stripInsightItem),
          habits: (actor.habits ?? []).map(stripInsightItem),
        });
      }
    }

    return {
      projectId,
      projectName,
      actors: exportedActors,
    };
  }

  /**
   * Export selected actors as a downloadable JSON envelope.
   */
  async exportSelectedActors(
    projectId: string,
    projectName: string,
    actorSelections: Array<{ actorId: string; audienceId: string }>,
  ): Promise<InsightsExportEnvelope> {
    const payload = await this.buildPayload(projectId, projectName, actorSelections);

    const totalInsightItems = payload.actors.reduce(
      (sum, actor) => sum + countInsightItems(actor),
      0,
    );

    return {
      __segmently_insights_export__: true,
      version: '1.0.0',
      type: 'analyze-insights',
      payload,
      metadata: {
        exportedAt: new Date().toISOString(),
        projectId,
        actorCount: payload.actors.length,
        totalInsightItems,
      },
    };
  }

  /**
   * Download JSON data as a file (browser-side).
   */
  static download(data: unknown, filename: string): void {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}
